import type { Organization } from '@contexts/organizations-provider';

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { UseOrganizationSwitcherPopoverReturn } from './useOrganizationSwitcherPopover.types';

import {
  filterOrganizations,
  getNextActiveIndexSkippingSelected,
  isNavigationKey,
  type NavigationKey,
} from '../../OrganizationSwitcher.utils';

export function useOrganizationSwitcherPopover(
  organizations: Organization[],
  currentOrg: Organization,
  isInternalUser: boolean
): UseOrganizationSwitcherPopoverReturn {
  const navigate = useNavigate();

  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(
    null
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const isOpen = Boolean(popoverAnchor);
  const showSearch = isInternalUser && organizations.length >= 10;

  const filteredOrganizations = React.useMemo(
    () => filterOrganizations(organizations, searchQuery),
    [organizations, searchQuery]
  );

  const handleClosePopover = React.useCallback((): void => {
    setPopoverAnchor(null);
    setSearchQuery('');
    setActiveIndex(-1);
  }, []);

  const handleTogglePopover = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (popoverAnchor) {
        handleClosePopover();
      } else {
        setPopoverAnchor(event.currentTarget);
        setActiveIndex(-1);
        setSearchQuery('');
      }
    },
    [popoverAnchor, handleClosePopover]
  );

  const handleOrganizationSelect = React.useCallback(
    (org: Organization): void => {
      handleClosePopover();
      navigate(`/manage/${org.customerId}/home`, { replace: true });
    },
    [navigate, handleClosePopover]
  );

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchQuery(event.target.value);
      setActiveIndex(-1);
    },
    []
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent): void => {
      if (!isOpen) return;

      const itemCount = filteredOrganizations.length;
      const selectedIndex = filteredOrganizations.findIndex(
        org => org.id === currentOrg.id
      );

      if (isNavigationKey(event.key)) {
        event.preventDefault();
        setActiveIndex(prev =>
          getNextActiveIndexSkippingSelected(
            event.key as NavigationKey,
            prev,
            itemCount,
            selectedIndex
          )
        );
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < itemCount) {
          const targetOrg = filteredOrganizations[activeIndex];
          if (targetOrg.id !== currentOrg.id) {
            handleOrganizationSelect(targetOrg);
          }
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        handleClosePopover();
      }
    },
    [
      isOpen,
      filteredOrganizations,
      activeIndex,
      currentOrg.id,
      handleOrganizationSelect,
      handleClosePopover,
    ]
  );

  const activeDescendant =
    activeIndex >= 0
      ? `org-option-${filteredOrganizations[activeIndex]?.id}`
      : undefined;

  return {
    isOpen,
    popoverAnchor,
    searchQuery,
    activeIndex,
    filteredOrganizations,
    showSearch,
    activeDescendant,
    handleTogglePopover,
    handleClosePopover,
    handleOrganizationSelect,
    handleSearchChange,
    handleKeyDown,
    setActiveIndex,
  };
}
